import { Filter } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { DatePickerWithPresets } from "@/shared/components/range-picker/RangePicker";
import { DynamicSelectFilter } from "@/shared/components/filters/Filters";
import type { Airport, SystemDictionary } from "@/shared/api/filters";
import { useState, type Dispatch, type SetStateAction } from "react";
import type { ManualFlightdFiltersState } from "../../pages/ManualFlightsPage";

interface ManualFlightsFiltersProps {
  totalCount: number;
  filteredCount: number;
  dictionary: SystemDictionary | undefined;
  setFilters: Dispatch<SetStateAction<ManualFlightdFiltersState>>;
  filters: ManualFlightdFiltersState;
  airports: Airport[];
}

const MANUAL_STATUSES = [
  { code: "draft", label: "Черновик" },
  { code: "cancelled", label: "Отменен" },
  { code: "active", label: "Активный" },
];

export function ManualFlightsFilters({
  totalCount,
  filteredCount,
  dictionary,
  setFilters,
  filters,
  airports,
}: ManualFlightsFiltersProps) {
  // Функция для обновления конкретного фильтра
  const updateFilter = (key: string, value: string) => {
    // Если выбрали "all", сбрасываем фильтр (передаем пустую строку)
    setFilters((prev) => ({ ...prev, [key]: value === "all" ? "" : value }));
  };

  const resetFilters = () => {
    setFilters({
      airline: "",
      status: "",
      page: 1,
      paymentStatus: "",
      pageSize: 20,
      dateFrom: "",
      dateTo: "",
      toIata: "",
      fromIata: "",
    });
  };

  const handleDateChange = (dateFrom: string, dateTo: string) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo, page: 1 }));
  };

  if (!dictionary) return null;

  return (
    <div className="bg-white rounded-t-lg border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Filter className="w-5 h-5 text-gray-500" />

        <DatePickerWithPresets
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          onChange={handleDateChange}
        />

        {/* 1. Пример с массивом простых строк (airlines) */}
        <DynamicSelectFilter
          placeholder="Авиакомпания"
          data={dictionary.airlines}
          value={filters.airline || undefined}
          onChange={(val) => updateFilter("airline", val)}
          // Для простых строк value и label одинаковые
          valueExtractor={(item) => item}
          labelExtractor={(item) => item}
        />

        <DynamicSelectFilter
          placeholder="Статус"
          data={MANUAL_STATUSES}
          value={filters.status || undefined}
          onChange={(val) => updateFilter("status", val)}
          valueExtractor={(item) => item.code}
          labelExtractor={(item) => item.label}
        />
        <DynamicSelectFilter
          placeholder="Вылет"
          searchPlaceholder="Поиск по аэропорту..."
          data={airports}
          value={filters.fromIata || undefined}
          onChange={(val) => updateFilter("fromIata", val)}
          valueExtractor={(item) => item.IATA}
          labelExtractor={(item) => `${item.IATA} — ${item.City.ru}`}
        />
        <DynamicSelectFilter
          placeholder="Прилет"
          searchPlaceholder="Поиск по аэропорту..."
          data={airports}
          value={filters.toIata || undefined}
          onChange={(val) => updateFilter("toIata", val)}
          valueExtractor={(item) => item.IATA}
          labelExtractor={(item) => `${item.IATA} — ${item.City.ru}`}
        />

        <Button
          variant="ghost"
          size="sm"
          className="h-10"
          onClick={resetFilters}
        >
          Сброс фильтров
        </Button>
        <span className="text-sm text-gray-600 ml-auto">
          Показано {filteredCount} из {totalCount}
        </span>
      </div>
    </div>
  );
}
